/**
 * The fixed timestep, and the inputs it consumes.
 *
 * One tick is 1/60 s. Within a tick the order is always: inputs, then gravity
 * and the lock timer, then the tick counter, then any garbage whose delay has
 * run out. Nothing about that order depends on wall time, so a replay of the
 * same events at the same frames produces the same state — which is the whole
 * regression story.
 *
 * When a well has more than one active piece, the order *within* those two
 * halves is by slot, ascending: every one of slot 0's inputs, then every one of
 * slot 1's, then gravity for slot 0, then gravity for slot 1. That is the
 * arbitration rule for two players who reached for the same cell on the same
 * tick — see `inSlotOrder` below and `docs/decisions/0029`.
 *
 * DAS and ARR are not here and never will be. By the time an input reaches the
 * engine it is one discrete action; auto-repeat is the client's job (phase 02).
 */
import {
  GRAVITY_PERMILLE,
  gravityThresholdUnits,
  gravityUnitsPerTick,
  lockDelayTicks,
} from './config.js';
import { releaseDueGarbage } from './garbage.js';
import { hardDrop, lockPiece } from './lock.js';
import { collides, holdPiece, tryMove, tryRotate } from './piece.js';
import type {
  GameState,
  InputAction,
  InputEvent,
  InputOutcome,
  LockResult,
  StepResult,
} from './types.js';

const NO_EVENTS: readonly InputEvent[] = [];

/**
 * The events of one tick, in the order they are allowed to happen.
 *
 * **Ascending slot order, stably within a slot.** This is the arbitration rule
 * `docs/prompts/06-coop.md` asks for: when two players reach for the same cell
 * on the same tick, the lower slot resolves first and the higher slot finds the
 * cell taken — because the second `tryMove` is evaluated against the state the
 * first one left. Nothing here depends on which order the *caller* collected
 * them in, which is what makes two peers that received the same tick's events
 * over different sockets produce the same board.
 *
 * A one-slot well returns the array untouched: every event's slot is 0, so
 * there is nothing to reorder and nothing about single-piece play moves.
 */
function inSlotOrder(events: readonly InputEvent[], slots: number): readonly InputEvent[] {
  if (slots < 2 || events.length < 2) return events;
  return [...events].sort((a, b) => (a.slot ?? 0) - (b.slot ?? 0));
}

/**
 * Apply one input to one slot.
 *
 * A topped-out game accepts nothing. Every action reports whether it did
 * anything; only a hard drop can also report a lock.
 */
export function applyInput(state: GameState, action: InputAction, slot = 0): InputOutcome {
  if (state.topOut) return { applied: false, lock: null };

  switch (action) {
    case 'left':
      return { applied: tryMove(state, slot, -1, 0), lock: null };
    case 'right':
      return { applied: tryMove(state, slot, 1, 0), lock: null };
    case 'softDrop':
      // Soft drop is a move, not a lock. It never forces a piece down early.
      return { applied: tryMove(state, slot, 0, 1), lock: null };
    case 'rotateCW':
      return { applied: tryRotate(state, slot, 1), lock: null };
    case 'rotateCCW':
      return { applied: tryRotate(state, slot, -1), lock: null };
    case 'hold':
      return { applied: holdPiece(state, slot), lock: null };
    case 'hardDrop': {
      const lock = hardDrop(state, slot);
      return { applied: lock !== null, lock };
    }
  }
}

/**
 * Run the coming tick's gravity at `modifierPermille` thousandths of this
 * game's own speed, for every active piece.
 *
 * This is how something outside the config changes how fast pieces fall: a
 * handicap easing one seat's gravity for a round, a curse doubling it for a
 * few seconds. The caller applies it once, before the `step` it is for, and
 * `step` then adds the game's own speed as it always does; the two together
 * come to `speedPermille * modifierPermille` units, an integer because both
 * factors are. `GRAVITY_PERMILLE` changes nothing.
 *
 * It refuses a modifier that is not a non-negative integer rather than let a
 * fraction into hashed state.
 */
export function applyGravityModifier(state: GameState, modifierPermille: number): void {
  if (!Number.isSafeInteger(modifierPermille) || modifierPermille < 0) {
    throw new RangeError(
      `gravity modifier must be a non-negative integer of thousandths, got ${String(modifierPermille)}`,
    );
  }
  const extra = state.config.gravity.speedPermille * (modifierPermille - GRAVITY_PERMILLE);
  if (extra === 0) return;
  for (const piece of state.pieces) {
    if (piece !== null) piece.gravityUnits += extra;
  }
}

/** Gravity and the lock timer for every slot, in slot order. */
function applyGravity(state: GameState, locks: LockResult[]): void {
  const threshold = gravityThresholdUnits(state);
  const perTick = gravityUnitsPerTick(state.config);
  const lockDelay = lockDelayTicks(state.config);

  for (const [slot, piece] of state.pieces.entries()) {
    if (piece === null) continue;

    piece.gravityUnits += perTick;
    while (piece.gravityUnits >= threshold) {
      piece.gravityUnits -= threshold;
      if (!tryMove(state, slot, 0, 1)) break;
    }

    if (collides(state, piece.kind, piece.rotation, piece.x, piece.y + 1, slot)) {
      piece.lockTicks += 1;
      if (piece.lockTicks >= lockDelay) {
        const result = lockPiece(state, slot);
        if (result !== null) locks.push(result);
      }
    } else {
      piece.lockTicks = 0;
    }
  }
}

/** One tick. The events given are the events for this tick. */
function runTick(state: GameState, events: readonly InputEvent[], locks: LockResult[]): number {
  if (state.topOut) {
    state.tick += 1;
    return 0;
  }

  for (const event of inSlotOrder(events, state.pieces.length)) {
    const outcome = applyInput(state, event.action, event.slot ?? 0);
    if (outcome.lock !== null) locks.push(outcome.lock);
  }

  applyGravity(state, locks);

  // The counter moves before garbage is released so that a batch queued with a
  // delay of n ticks lands on the nth tick advanced, which is what a caller who
  // wrote `queueGarbage(state, 4, 30)` expects half a second later.
  state.tick += 1;
  return releaseDueGarbage(state);
}

/**
 * Advance exactly one tick, applying every event given.
 *
 * This is the primitive. The events' `frame` is not consulted, because the
 * caller has already decided they belong to this tick; they are applied in
 * ascending slot order, and in the order given within a slot (`inSlotOrder`).
 * Use `advance` to let the engine do the bucketing.
 */
export function step(state: GameState, events: readonly InputEvent[] = NO_EVENTS): StepResult {
  const locks: LockResult[] = [];
  const garbage = runTick(state, events, locks);
  return {
    tick: state.tick,
    ticksAdvanced: 1,
    eventsApplied: events.length,
    locks,
    garbageApplied: garbage,
    topOut: state.topOut,
  };
}

/**
 * Advance `ticks` ticks, delivering each event on the tick its `frame` names.
 *
 * An event whose frame has already gone by is applied on the first tick of the
 * call — a late input still counts. An event scheduled past the end of the
 * window is not applied at all, and is not counted in `eventsApplied`; call
 * again with a longer window and it will land on its frame. Events sharing a
 * frame are applied in ascending slot order, and in the order given within a
 * slot.
 */
export function advance(
  state: GameState,
  ticks: number,
  events: readonly InputEvent[] = NO_EVENTS,
): StepResult {
  const startTick = state.tick;
  const target = startTick + Math.max(0, ticks);

  const buckets = new Map<number, InputEvent[]>();
  let eventsApplied = 0;
  for (const event of events) {
    const frame = Math.max(event.frame, startTick);
    if (frame >= target) continue;
    const bucket = buckets.get(frame);
    if (bucket === undefined) buckets.set(frame, [event]);
    else bucket.push(event);
    eventsApplied += 1;
  }

  const locks: LockResult[] = [];
  let garbage = 0;
  while (state.tick < target) {
    garbage += runTick(state, buckets.get(state.tick) ?? NO_EVENTS, locks);
  }

  return {
    tick: state.tick,
    ticksAdvanced: state.tick - startTick,
    eventsApplied,
    locks,
    garbageApplied: garbage,
    topOut: state.topOut,
  };
}
